import React, { useRef, useEffect, useState, RefObject } from 'react';
import { IconButton } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';

interface Column {
  count: number;
  name: string;
  items: any[];
}

interface Columns {
  [key: string]: Column;
}

interface KanbanMinimapProps {
  columns: Columns | null;
  containerRef: RefObject<HTMLDivElement>;
}

const KanbanMinimap: React.FC<KanbanMinimapProps> = ({ columns, containerRef }) => {
  const minimapRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [viewportPosition, setViewportPosition] = useState<number>(0);
  const [viewportWidth, setViewportWidth] = useState<number>(0);
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [zoomLevel, setZoomLevel] = useState<number>(() => {
    const saved = localStorage.getItem('kanban-zoom-level');
    return saved ? parseInt(saved, 10) : 100;
  });

  useEffect(() => {
    (document.body.style as any).zoom = `${zoomLevel}%`;
    localStorage.setItem('kanban-zoom-level', zoomLevel.toString());
  }, [zoomLevel]);

  const handleZoomIn = () => setZoomLevel(prev => Math.min(prev + 10, 150));
  const handleZoomOut = () => setZoomLevel(prev => Math.max(prev - 10, 50));

  const updateViewport = (): void => {
    if (!containerRef?.current || !contentRef?.current) return;
    
    const container = containerRef.current;
    const minimapContent = contentRef.current;
    
    const scrollLeft = container.scrollLeft;
    const scrollWidth = container.scrollWidth;
    const clientWidth = container.clientWidth;
    
    // Prevent division by zero
    if (scrollWidth === 0 || clientWidth === 0) return;
    
    // Calculate position as percentage of the minimap content width
    const scrollPercentage = scrollLeft / scrollWidth;
    const viewportPercentage = clientWidth / scrollWidth;
    
    const minimapContentWidth = minimapContent.offsetWidth;
    
    setViewportPosition(scrollPercentage * minimapContentWidth);
    setViewportWidth(viewportPercentage * minimapContentWidth);
  };

  useEffect(() => {
    const container = containerRef?.current;
    if (!container) return;

    container.addEventListener('scroll', updateViewport);
    
    const resizeObserver = new ResizeObserver(updateViewport);
    resizeObserver.observe(container);
    
    // Initial update
    setTimeout(updateViewport, 100);

    return () => {
      container.removeEventListener('scroll', updateViewport);
      resizeObserver.disconnect();
    };
  }, [containerRef, columns]);

  const handleMinimapClick = (e: React.MouseEvent<HTMLDivElement>): void => {
    if (!containerRef?.current || !contentRef?.current) return;
    
    const minimapContent = contentRef.current;
    const rect = minimapContent.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const clickPercentage = clickX / minimapContent.offsetWidth;
    
    const container = containerRef.current;
    const scrollWidth = container.scrollWidth;
    const targetScroll = clickPercentage * scrollWidth - (container.clientWidth / 2);
    
    container.scrollTo({
      left: Math.max(0, Math.min(targetScroll, scrollWidth - container.clientWidth)),
      behavior: 'smooth'
    });
  };

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>): void => {
    if ((e.target as HTMLElement).closest('.minimap-controls')) return;
    setIsDragging(true);
    handleMinimapClick(e);
  };

  const handleMouseMove = (e: MouseEvent): void => {
    if (!isDragging) return;
    handleMinimapClick(e as any);
  };

  const handleMouseUp = (): void => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging]);

  const columnCount = columns ? Object.keys(columns).length : 0;

  if (columnCount === 0 || !columns) return null;

  const getVisibleCards = (count: number): number => Math.min(count, 5);

  // Calculate column width to fit all columns in view
  const minimapContentWidth = 84; // 100 - 16px padding
  const columnGap = 3;
  const totalGaps = (columnCount - 1) * columnGap;
  const columnWidth = Math.max(8, Math.min(20, (minimapContentWidth - totalGaps) / columnCount));

  return (
    <div style={{ position: 'fixed', bottom: '9px', right: '8px', zIndex: 1200 }}>
      <div style={{ display: 'flex', alignItems: 'stretch', gap: '3px' }}>
        {/* Minimap */}
        <div
          ref={minimapRef}
          style={{
            width: '100px',
            height: '100px',
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(8px)',
            borderRadius: '8px',
            overflow: 'hidden',
            cursor: isDragging ? 'grabbing' : 'grab',
            border: '1px solid #d0d0d0',
            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
          }}
        >
          {/* Minimap Board View */}
          <div
            style={{
              height: '100%',
              position: 'relative',
              overflow: 'hidden',
              padding: '8px 6px',
            }}
            onMouseDown={handleMouseDown}
          >
            {/* All Columns Fitted */}
            <div
              ref={contentRef}
              style={{
                display: 'flex',
                gap: `${columnGap}px`,
                height: '100%',
                justifyContent: 'flex-start',
              }}
            >
              {Object.entries(columns).map(([key, column]) => {
                const visibleCardCount = getVisibleCards(column.count);
                
                return (
                  <div
                    key={key}
                    style={{
                      width: `${columnWidth}px`,
                      minWidth: `${columnWidth}px`,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '2px',
                    }}
                  >
                    {/* Column Header - Count only */}
                    <div
                      style={{
                        background: '#f5f5f5',
                        borderRadius: '2px',
                        padding: '2px 1px',
                        textAlign: 'center',
                        border: '1px solid #e0e0e0',
                      }}
                    >
                      <div style={{ fontSize: '7px', fontWeight: 600, color: '#666' }}>
                        {column.count}
                      </div>
                    </div>

                    {/* Column Cards - Simple gray bars */}
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '2px',
                        flex: 1,
                      }}
                    >
                      {column.items.slice(0, visibleCardCount).map((item, cardIndex) => {
                        return (
                          <div
                            key={cardIndex}
                            style={{
                              height: '5px',
                              background: '#e8e8e8',
                              borderRadius: '1px',
                              border: '1px solid #d0d0d0',
                            }}
                          />
                        );
                      })}
                      
                      {/* Show ellipsis if more cards exist */}
                      {column.count > 5 && (
                        <div
                          style={{
                            fontSize: '6px',
                            textAlign: 'center',
                            color: '#999',
                            fontWeight: 600,
                          }}
                        >
                          +{column.count - 5}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            
            {/* Viewport Indicator - Blue border rectangle */}
            <div
              style={{
                position: 'absolute',
                left: `${viewportPosition + 7}px`,
                top: '8px',
                width: `${viewportWidth}px`,
                height: 'calc(100% - 16px)',
                background: 'transparent',
                border: '2px solid #1976d2',
                borderRadius: '4px',
                pointerEvents: 'none',
                transition: isDragging ? 'none' : 'left 0.15s ease-out, width 0.15s ease-out',
                zIndex: 10,
              }}
            />
          </div>
        </div>

        {/* Zoom Controls - Now on the right side */}
        <div
          className="minimap-controls"
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            height: '100px',
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(8px)',
            borderRadius: '6px',
            padding: '6px 4px',
            border: '1px solid #d0d0d0',
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
          }}
        >
          <IconButton size="small" onClick={handleZoomIn} disabled={zoomLevel >= 150} sx={{ padding: '2px' }}>
            <AddIcon sx={{ fontSize: 16 }} />
          </IconButton>
          <div style={{ 
            fontSize: '10px', 
            fontWeight: 600, 
            textAlign: 'center',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flex: 1,
            writingMode: 'vertical-rl',
            transform: 'rotate(180deg)'
          }}>
            {zoomLevel}%
          </div>
          <IconButton size="small" onClick={handleZoomOut} disabled={zoomLevel <= 50} sx={{ padding: '2px' }}>
            <RemoveIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </div>
      </div>
    </div>
  );
};

export default KanbanMinimap;